import React from 'react';
import { Activity, Archive, Download, FileText, RefreshCw, TrendingUp, UserCheck, UserPlus, Users } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { TabsContent } from '../../../components/ui/tabs';

export const ExecutiveTab = ({ analytics, executiveReport, exportReportToPDF, loadExecutiveReport, loadingReport }) => (
            <TabsContent value="executive">
              <div className="space-y-6">
                {/* Header with Export */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Executive Report</h2>
                    <p className="text-muted-foreground">Comprehensive analytics for leadership review</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={loadExecutiveReport} disabled={loadingReport}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingReport ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button onClick={exportReportToPDF} disabled={!executiveReport}>
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  </div>
                </div>
  
                {loadingReport && !executiveReport ? (
                  <div className="text-center py-12 text-muted-foreground">Loading executive report...</div>
                ) : executiveReport ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3">
                            <Users className="w-8 h-8 text-teal-600" />
                            <div>
                              <p className="text-3xl font-bold text-teal-700">{executiveReport.summary.total_users}</p>
                              <p className="text-sm text-teal-600">Total Users</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8 text-blue-600" />
                            <div>
                              <p className="text-3xl font-bold text-blue-700">{executiveReport.summary.active_tacticals}</p>
                              <p className="text-sm text-blue-600">Active Tacticals</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3">
                            <Archive className="w-8 h-8 text-purple-600" />
                            <div>
                              <p className="text-3xl font-bold text-purple-700">{executiveReport.summary.archived_tacticals}</p>
                              <p className="text-sm text-purple-600">Archived</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3">
                            <UserCheck className="w-8 h-8 text-orange-600" />
                            <div>
                              <p className="text-3xl font-bold text-orange-700">{executiveReport.summary.manager_relationships}</p>
                              <p className="text-sm text-orange-600">Manager Links</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
  
                    {/* Growth Metrics Table */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                          Growth Metrics by Period
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-slate-50">
                                <th className="py-3 px-4 text-left font-semibold">Period</th>
                                <th className="py-3 px-4 text-center font-semibold">New Users</th>
                                <th className="py-3 px-4 text-center font-semibold">Active Logins</th>
                                <th className="py-3 px-4 text-center font-semibold">Tacticals Created</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b hover:bg-slate-50">
                                <td className="py-3 px-4 font-medium">Last 30 Days</td>
                                <td className="py-3 px-4 text-center">{executiveReport.user_growth['30_days']}</td>
                                <td className="py-3 px-4 text-center">{executiveReport.login_activity['30_days']}</td>
                                <td className="py-3 px-4 text-center">{executiveReport.tactical_creation['30_days']}</td>
                              </tr>
                              <tr className="border-b hover:bg-slate-50">
                                <td className="py-3 px-4 font-medium">Last 60 Days</td>
                                <td className="py-3 px-4 text-center">{executiveReport.user_growth['60_days']}</td>
                                <td className="py-3 px-4 text-center">{executiveReport.login_activity['60_days']}</td>
                                <td className="py-3 px-4 text-center">{executiveReport.tactical_creation['60_days']}</td>
                              </tr>
                              <tr className="border-b hover:bg-slate-50">
                                <td className="py-3 px-4 font-medium">Last 90 Days</td>
                                <td className="py-3 px-4 text-center">{executiveReport.user_growth['90_days']}</td>
                                <td className="py-3 px-4 text-center">{executiveReport.login_activity['90_days']}</td>
                                <td className="py-3 px-4 text-center">{executiveReport.tactical_creation['90_days']}</td>
                              </tr>
                              <tr className="hover:bg-slate-50 bg-emerald-50">
                                <td className="py-3 px-4 font-bold">Last 12 Months</td>
                                <td className="py-3 px-4 text-center font-bold">{executiveReport.user_growth.yearly}</td>
                                <td className="py-3 px-4 text-center font-bold">{executiveReport.login_activity.yearly}</td>
                                <td className="py-3 px-4 text-center font-bold">{executiveReport.tactical_creation.yearly}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
  
                    {/* Scorecard Health */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Scorecard Health Overview</CardTitle>
                        <CardDescription>Distribution of scorecard statuses across all active tacticals</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex h-8 rounded-lg overflow-hidden mb-4">
                          <div className="bg-emerald-500 flex items-center justify-center text-white text-sm font-medium" style={{width: `${executiveReport.scorecard_health.green_pct}%`}}>
                            {executiveReport.scorecard_health.green_pct > 10 && `${executiveReport.scorecard_health.green_pct}%`}
                          </div>
                          <div className="bg-yellow-500 flex items-center justify-center text-white text-sm font-medium" style={{width: `${executiveReport.scorecard_health.yellow_pct}%`}}>
                            {executiveReport.scorecard_health.yellow_pct > 10 && `${executiveReport.scorecard_health.yellow_pct}%`}
                          </div>
                          <div className="bg-red-500 flex items-center justify-center text-white text-sm font-medium" style={{width: `${executiveReport.scorecard_health.red_pct}%`}}>
                            {executiveReport.scorecard_health.red_pct > 10 && `${executiveReport.scorecard_health.red_pct}%`}
                          </div>
                        </div>
                        <div className="flex justify-around text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-emerald-500 rounded"></div>
                            <span>Green: {executiveReport.scorecard_health.green} ({executiveReport.scorecard_health.green_pct}%)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                            <span>Yellow: {executiveReport.scorecard_health.yellow} ({executiveReport.scorecard_health.yellow_pct}%)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-500 rounded"></div>
                            <span>Red: {executiveReport.scorecard_health.red} ({executiveReport.scorecard_health.red_pct}%)</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
  
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Manager Engagement */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-blue-600" />
                            Manager Engagement
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-muted-foreground">Total Relationships</span>
                              <span className="font-bold text-lg">{executiveReport.manager_engagement.total_relationships}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-muted-foreground">Unique Managers</span>
                              <span className="font-bold text-lg">{executiveReport.manager_engagement.unique_managers}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-muted-foreground">Employees with Managers</span>
                              <span className="font-bold text-lg">{executiveReport.manager_engagement.unique_employees_with_managers}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                              <span className="text-muted-foreground">Pending Invitations</span>
                              <Badge variant={executiveReport.manager_engagement.pending_invitations > 0 ? "destructive" : "secondary"}>
                                {executiveReport.manager_engagement.pending_invitations}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
  
                      {/* System Health */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Activity className="w-5 h-5 text-purple-600" />
                            System Health (30 Days)
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-muted-foreground">Total Activity Events</span>
                              <span className="font-bold text-lg">{executiveReport.system_health.activity_events_30d}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-muted-foreground">Errors Logged</span>
                              <span className="font-bold text-lg text-red-600">{executiveReport.system_health.errors_30d}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                              <span className="text-muted-foreground">Error Rate</span>
                              <Badge variant={executiveReport.system_health.error_rate > 5 ? "destructive" : "secondary"}>
                                {executiveReport.system_health.error_rate}%
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
  
                    {/* Top Engaged Users */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Top Engaged Users</CardTitle>
                        <CardDescription>Users with the most tactical activity</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-slate-50">
                                <th className="py-3 px-4 text-left font-semibold">Rank</th>
                                <th className="py-3 px-4 text-left font-semibold">Name</th>
                                <th className="py-3 px-4 text-left font-semibold">Email</th>
                                <th className="py-3 px-4 text-center font-semibold">Total Tacticals</th>
                              </tr>
                            </thead>
                            <tbody>
                              {executiveReport.top_engaged_users.map((user, index) => (
                                <tr key={user.id || user.email || index} className="border-b hover:bg-slate-50">
                                  <td className="py-3 px-4">
                                    <Badge variant={index < 3 ? "default" : "secondary"}>{index + 1}</Badge>
                                  </td>
                                  <td className="py-3 px-4 font-medium">{user.name}</td>
                                  <td className="py-3 px-4 text-muted-foreground">{user.email}</td>
                                  <td className="py-3 px-4 text-center font-bold">{user.tactical_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
  
                    {/* Report Footer */}
                    <div className="text-center text-sm text-muted-foreground py-4 border-t">
                      <p>Report generated: {new Date(executiveReport.generated_at).toLocaleString()}</p>
                      <p className="mt-1">TMC Therapy Growth Hub - Confidential Executive Report</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Button onClick={loadExecutiveReport}>Load Executive Report</Button>
                  </div>
                )}
              </div>
            </TabsContent>
);
